import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuditEvents } from '../hooks/useAuditEvents.ts';
import { useOrganizations, useOrgMembers } from '../hooks/useOrganizations.ts';
import { TableSkeleton } from '../components/shared/LoadingSkeleton.tsx';
import { EmptyState } from '../components/shared/EmptyState.tsx';
import { toast } from 'react-hot-toast';
import { getErrorMessage } from '../utils/errorUtils.ts';
import {
  fetchAuditEvents,
  fetchOrgAuditEvents,
  type AuditEvent,
  type AuditEventListParams,
} from '../api/audit.api.ts';
import { fetchOrgAdminMembers } from '../../request/orgAdmin/orgAdmin.api.ts';
import { Download, X } from 'lucide-react';
import * as XLSX from 'xlsx';

export type AuditLogMode = 'platform' | 'org';

type AuditLogProps = {
  mode?: AuditLogMode;
};

function getStoredOrganizationId(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('organization_id') ?? '';
}

const ACTION_STYLES: Record<string, string> = {
  CREATE: 'text-emerald-700 bg-emerald-50',
  UPDATE: 'text-amber-700 bg-amber-50',
  DELETE: 'text-red-700 bg-red-50',
};

function formatWhen(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function prettyJson(value: unknown): string {
  if (value == null) return '—\n(none)';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const AuditLog: React.FC<AuditLogProps> = ({ mode = 'platform' }) => {
  const isOrgMode = mode === 'org';
  const currentOrgId = isOrgMode ? getStoredOrganizationId() : '';

  const [search, setSearch] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [actorUserId, setActorUserId] = useState('');
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data: orgs } = useOrganizations({ enabled: !isOrgMode });
  const platformMembersQuery = useOrgMembers(!isOrgMode ? (organizationId || undefined) : undefined);
  const orgMembersQuery = useQuery({
    queryKey: ['org-admin', 'members', currentOrgId, 'audit-filter'],
    queryFn: () => fetchOrgAdminMembers(currentOrgId),
    enabled: isOrgMode && Boolean(currentOrgId),
  });
  const members = isOrgMode ? (orgMembersQuery.data ?? []) : (platformMembersQuery.data ?? []);
  const membersLoading = isOrgMode ? orgMembersQuery.isFetching : platformMembersQuery.isFetching;

  const dateRangeInvalid = Boolean(fromDate && toDate && toDate < fromDate);

  const nextParams = useMemo<AuditEventListParams>(
    () => ({
      search: search.trim() || undefined,
      organization_id: isOrgMode ? undefined : (organizationId || undefined),
      actor_user_id: actorUserId || undefined,
      action: action || undefined,
      resource_type: resourceType || undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
      page,
      page_size: 20,
    }),
    [search, organizationId, actorUserId, action, resourceType, fromDate, toDate, page, isOrgMode],
  );

  // Keep last valid filters so a bad date range never blanks the page / hits the API.
  const lastValidParamsRef = useRef(nextParams);
  if (!dateRangeInvalid) {
    lastValidParamsRef.current = nextParams;
  }
  const params = dateRangeInvalid ? lastValidParamsRef.current : nextParams;

  const { data, isLoading, error, refetch, isFetching } = useAuditEvents(params, {
    enabled: !dateRangeInvalid,
    scope: isOrgMode ? 'org' : 'platform',
  });

  useEffect(() => {
    if (dateRangeInvalid) {
      toast.error('To date must be on or after from date');
    }
  }, [dateRangeInvalid, fromDate, toDate]);

  useEffect(() => {
    if (error && !dateRangeInvalid) {
      toast.error(getErrorMessage(error, 'Failed to load audit events'));
    }
  }, [error, dateRangeInvalid]);

  useEffect(() => {
    setSelected(null);
  }, [params]);

  const events = data?.events ?? [];
  const counts = data?.counts ?? { total: 0, create: 0, update: 0, delete: 0 };
  const pagination = data?.pagination;

  // Static list matching backend audit_log/common resource type constants.
  const resourceOptions = [
    'ai_engagement',
    'compliance_score',
    'itgc_domain',
    'job',
    'materiality_assessment',
    'process',
    'rcm',
    'report',
    'risk',
    'sof_comparison',
    'sox_control',
    'sox_cycle',
    'sox_issue',
    'system',
    'test_execution',
    'user_management',
  ] as const;

  const clearFilters = () => {
    setSearch('');
    setOrganizationId('');
    setActorUserId('');
    setAction('');
    setResourceType('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const handleExportExcel = async () => {
    if (fromDate && toDate && toDate < fromDate) {
      toast.error('To date must be on or after from date');
      return;
    }
    setExporting(true);
    try {
      const listFn = isOrgMode ? fetchOrgAuditEvents : fetchAuditEvents;
      const filterParams = {
        search: search.trim() || undefined,
        organization_id: isOrgMode ? undefined : (organizationId || undefined),
        actor_user_id: actorUserId || undefined,
        action: action || undefined,
        resource_type: resourceType || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      };
      const rows: AuditEvent[] = [];
      let exportPage = 1;
      let totalPages = 1;
      do {
        const res = await listFn({
          ...filterParams,
          page: exportPage,
          page_size: 100,
        });
        rows.push(...(res.events ?? []));
        totalPages = res.pagination?.total_pages || 1;
        exportPage += 1;
      } while (exportPage <= totalPages);

      if (rows.length === 0) {
        toast.error('No events to export for these filters');
        return;
      }

      const excelData = rows.map((e) => ({
        When: formatWhen(e.created_at),
        Action: e.action,
        Resource: e.resource_type,
        'Resource ID': e.resource_id || '',
        'Resource Label': e.resource_label || '',
        Summary: e.summary,
        Actor: e.actor_username || '',
        'Actor Role': e.actor_role || '',
        Organization: e.organization_name || '',
        'Request ID': e.request_id || '',
        'Duration (ms)': e.duration_ms ?? '',
        'HTTP Method': e.http_method || '',
        'HTTP Path': e.http_path || '',
        Before: e.before_data == null ? '' : JSON.stringify(e.before_data),
        After: e.after_data == null ? '' : JSON.stringify(e.after_data),
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 22 }, { wch: 10 }, { wch: 18 }, { wch: 36 }, { wch: 24 },
        { wch: 40 }, { wch: 16 }, { wch: 14 }, { wch: 22 }, { wch: 28 },
        { wch: 12 }, { wch: 10 }, { wch: 28 }, { wch: 40 }, { wch: 40 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Log');
      const timestamp = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `audit_events_${timestamp}.xlsx`);
      toast.success('Excel downloaded');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to download Excel'));
    } finally {
      setExporting(false);
    }
  };

  if (isLoading && !data) {
    return <TableSkeleton rows={8} columns={6} />;
  }

  if (error && !data && !dateRangeInvalid) {
    return (
      <EmptyState
        title="Audit log unavailable"
        message="Please try again in a moment."
        action={(
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800"
          >
            Retry
          </button>
        )}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-600 mt-1">
            {isOrgMode
              ? 'Successful create / update / delete actions in this organization — who, what, when'
              : 'Successful create / update / delete actions across organizations — who, what, when'}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={handleExportExcel}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            disabled={exporting || isFetching || dateRangeInvalid}
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Download Excel'}
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-500">Events (filter total)</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{counts.total}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-500">Creates</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{counts.create}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-500">Updates</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{counts.update}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-500">Deletes</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{counts.delete}</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={isOrgMode ? 'User, resource, request id…' : 'User, resource, org, request id…'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        {!isOrgMode ? (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Organization</label>
            <select
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setActorUserId('');
                setPage(1);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">All orgs</option>
              {(orgs ?? []).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        ) : null}
        {(isOrgMode || organizationId) ? (
          <div>
            <label className="block text-xs text-gray-500 mb-1">User</label>
            <select
              value={actorUserId}
              onChange={(e) => {
                setActorUserId(e.target.value);
                setPage(1);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              disabled={membersLoading}
            >
              <option value="">{membersLoading ? 'Loading users…' : 'All users'}</option>
              {members.map((m) => {
                const uid = m.user_id || m.user?.id;
                if (!uid) return null;
                const label = m.user?.username || `User ${uid}`;
                return (
                  <option key={uid} value={String(uid)}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
        ) : null}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Action</label>
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">All actions</option>
            <option value="CREATE">CREATE</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Resource</label>
          <select
            value={resourceType}
            onChange={(e) => {
              setResourceType(e.target.value);
              setPage(1);
            }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">All resources</option>
            {resourceOptions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From date</label>
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
            className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
              dateRangeInvalid ? 'border-red-400' : 'border-gray-300'
            }`}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To date</label>
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
            className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
              dateRangeInvalid ? 'border-red-400' : 'border-gray-300'
            }`}
          />
        </div>
        <div className="md:col-span-6 flex justify-end">
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Activity</h2>
          <span className="text-xs text-gray-500">Click a row for detail</span>
        </div>
        {events.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">No events match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Resource</th>
                  <th className="px-4 py-3 font-medium">Summary</th>
                  <th className="px-4 py-3 font-medium">Actor</th>
                  <th className="px-4 py-3 font-medium">Organization</th>
                  <th className="px-4 py-3 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setSelected(e)}
                    className={`border-t border-gray-100 cursor-pointer hover:bg-slate-50 ${
                      selected?.id === e.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                      {formatWhen(e.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_STYLES[e.action] ?? 'bg-gray-100 text-gray-700'}`}>
                        {e.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{e.resource_type}</td>
                    <td className="px-4 py-3 text-gray-800 max-w-xs truncate" title={e.summary}>{e.summary}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{e.actor_username || '—'}</div>
                      <div className="text-xs text-gray-500">{e.actor_role || ''}</div>
                    </td>
                    <td className="px-4 py-3">{e.organization_name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {e.duration_ms != null ? `${e.duration_ms} ms` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && pagination.total_pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Page {pagination.page} of {pagination.total_pages} · {pagination.total_items} events
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <>
          <div
            className="fixed inset-0 bg-slate-900/30 z-40"
            onClick={() => setSelected(null)}
          />
          <aside className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">{selected.summary}</h3>
                <p className="text-xs text-gray-500 mt-1 font-mono truncate">
                  {selected.id}
                  {selected.http_method ? ` · ${selected.http_method} ${selected.http_path ?? ''}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="h-8 w-8 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
              <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2">
                <dt className="text-gray-500">When</dt>
                <dd className="font-mono text-xs">{formatWhen(selected.created_at)}</dd>
                <dt className="text-gray-500">Action</dt>
                <dd>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_STYLES[selected.action] ?? 'bg-gray-100 text-gray-700'}`}>
                    {selected.action}
                  </span>
                </dd>
                <dt className="text-gray-500">Resource</dt>
                <dd className="font-mono text-xs break-all">
                  {selected.resource_type}
                  {selected.resource_id ? ` · ${selected.resource_id}` : ''}
                  {selected.resource_label ? ` (${selected.resource_label})` : ''}
                </dd>
                <dt className="text-gray-500">Actor</dt>
                <dd>
                  {selected.actor_username || '—'}
                  {selected.actor_role ? (
                    <span className="block text-xs text-gray-500">{selected.actor_role}</span>
                  ) : null}
                </dd>
                <dt className="text-gray-500">Organization</dt>
                <dd>{selected.organization_name || selected.organization_id || '—'}</dd>
                <dt className="text-gray-500">Request</dt>
                <dd className="font-mono text-xs break-all">{selected.request_id || '—'}</dd>
                <dt className="text-gray-500">Duration</dt>
                <dd className="font-mono text-xs">
                  {selected.duration_ms != null ? `${selected.duration_ms} ms` : '—'}
                </dd>
              </dl>

              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Change snapshot</p>
                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <div className="text-[11px] font-bold uppercase text-gray-500 mb-1">Before</div>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-rose-50 border border-rose-100 rounded-lg p-2 min-h-[5rem]">
                      {prettyJson(selected.before_data)}
                    </pre>
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase text-gray-500 mb-1">After</div>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-emerald-50 border border-emerald-100 rounded-lg p-2 min-h-[5rem]">
                      {prettyJson(selected.after_data)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default AuditLog;
